import { getIdentity, isAllowedRedirect } from '@sdc/auth';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Splash } from '../components/Splash';
import { SynthwaveBg } from '../components/SynthwaveBg';
import { logAuditEvent } from '../lib/audit';
import { logoColor } from '../lib/logos';
import { useSessionWait } from '../lib/useSessionWait';

type Mode = 'password' | 'magic';

/**
 * Zielbestimmung nach dem Login:
 *  - `next` = interner Pfad (vom Session-Guard des Hubs)
 *  - `redirect` = absolute URL einer Schwester-App (von `RequireAuth`),
 *    nur eigene Subdomains erlaubt.
 */
function resolveTarget(params: URLSearchParams): { internal: string; external: string | null } {
  const redirect = params.get('redirect');
  if (redirect && isAllowedRedirect(redirect)) return { internal: '/', external: redirect };

  const next = params.get('next');
  if (next && next.startsWith('/') && !next.startsWith('//')) {
    return { internal: next, external: null };
  }
  return { internal: '/', external: null };
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const target = resolveTarget(params);

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const goToTarget = () => {
    if (target.external) window.location.replace(target.external);
    else navigate(target.internal, { replace: true });
  };

  // Schon angemeldet? Dann direkt weiter. Der Hook hat ein Timeout, damit die
  // Seite nicht im Spinner hängt, wenn die Initialisierung nicht durchläuft.
  const { state: sessionState } = useSessionWait(4000);

  useEffect(() => {
    if (sessionState === 'ready') goToTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState]);

  async function signInWithPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);

    const { error: authError } = await getIdentity().auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (authError) {
      setError(
        authError.message === 'Invalid login credentials'
          ? 'E-Mail oder Passwort stimmt nicht.'
          : authError.message,
      );
      setBusy(false);
      return;
    }

    await logAuditEvent('login', { method: 'password' });
    goToTarget();
  }

  async function sendMagicLink(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);

    const callback = new URL('/auth/callback', window.location.origin);
    if (target.external) callback.searchParams.set('redirect', target.external);
    else if (target.internal !== '/') callback.searchParams.set('next', target.internal);

    const { error: authError } = await getIdentity().auth.signInWithOtp({
      email: email.trim(),
      options: {
        // Kein Self-Signup: Accounts entstehen nur per Admin-Invite.
        shouldCreateUser: false,
        emailRedirectTo: callback.toString(),
      },
    });

    setBusy(false);
    if (authError) {
      setError(
        /signups not allowed|not found/i.test(authError.message)
          ? 'Für diese Adresse gibt es keinen Account. Bitte beim Admin melden.'
          : authError.message,
      );
      return;
    }
    setInfo(`Link ist unterwegs an ${email.trim()}. Er gilt 60 Minuten.`);
  }

  async function resetPassword() {
    if (!email.trim()) {
      setError('Bitte zuerst die E-Mail-Adresse eintragen.');
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);

    const { error: resetError } = await getIdentity().auth.resetPasswordForEmail(email.trim(), {
      redirectTo: new URL('/auth/callback', window.location.origin).toString(),
    });

    setBusy(false);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setInfo(`Wenn es einen Account gibt, ist eine Mail an ${email.trim()} unterwegs.`);
  }

  if (sessionState === 'waiting') {
    return (
      <>
        <SynthwaveBg />
        <Splash label="Session wird geprüft …" />
      </>
    );
  }

  return (
    <>
      <SynthwaveBg />
      <div className="auth">
        <div className="auth__card">
          <div className="auth__brand">
            <img src={logoColor} alt="" width={42} height={42} />
            <span>
              SDC <em>HUB</em>
            </span>
          </div>

          <h1>Anmelden</h1>
          <p className="auth__claim">// today's complexity. retro simplicity.</p>

          <div className="auth__tabs" role="tablist" aria-label="Anmeldeart">
            <button
              type="button"
              role="tab"
              className="auth__tab"
              aria-selected={mode === 'password'}
              onClick={() => {
                setMode('password');
                setError(null);
                setInfo(null);
              }}
            >
              Passwort
            </button>
            <button
              type="button"
              role="tab"
              className="auth__tab"
              aria-selected={mode === 'magic'}
              onClick={() => {
                setMode('magic');
                setError(null);
                setInfo(null);
              }}
            >
              Magic Link
            </button>
          </div>

          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}
          {info && (
            <div className="alert alert--success" role="status">
              {info}
            </div>
          )}

          <form onSubmit={mode === 'password' ? signInWithPassword : sendMagicLink}>
            <label className="field">
              <span className="field__label">E-Mail</span>
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vorname.nachname@…"
              />
            </label>

            {mode === 'password' && (
              <label className="field">
                <span className="field__label">Passwort</span>
                <input
                  className="input"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
            )}

            <button type="submit" className="px-btn px-btn--block" disabled={busy}>
              {busy ? 'Moment …' : mode === 'password' ? 'Anmelden' : 'Link senden'}
            </button>
          </form>

          {mode === 'password' && (
            <div className="auth__linkrow">
              <button type="button" className="linkbtn" onClick={() => void resetPassword()} disabled={busy}>
                Passwort vergessen?
              </button>
            </div>
          )}

          <p className="auth__hint">// kein self-signup · accounts legt ein admin an</p>
        </div>
      </div>
    </>
  );
}
